/**
 * KbSidebar — Folder/space navigation with recursive document tree.
 * Shows folders (spaces) with expandable document hierarchy inside each.
 * Matches PaaS KbSidebar.tsx pattern with TreeItem recursion.
 *
 * THE SERVER ANSWERS (Library LB6-T2, L6-15 / L6-17). The folders, and every
 * folder's badge, are the ONE folder list (GET /wiki/folders): its `count` is
 * how many documents of every kind this person may see in the folder, from
 * the Document visibility resolver's own predicate — the same one the list
 * applies, so a badge equals GET /knowledge/documents?folderId=<F>'s total.
 * "All Documents" is that route's total. The pages inside an open folder are
 * GET /wiki/articles?folderId=<F> — the server's, never the page's loaded
 * rows filtered by folder. An article being written and not yet saved is in
 * no list and changes no badge. A failed read is an error, never an empty
 * tree or a 0: a number that could not be read is not shown.
 *
 * ONE PERSONAL STAR (decision 38, LB6-T5). Every folder row carries THE
 * StarButton with kind 'folder' — the same component and the same store as a
 * document's star; a person who may read the Library may star (a star is
 * per-person, read:library — L6-02), so it is drawn for everyone who sees the
 * row, menu or no menu.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight, ChevronDown, FileText, FolderOpen, Folder, Plus,
  MoreHorizontal, Settings, Download, Trash2, BookOpen, Code2,
  Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/lib/use-has-permission';
import { FolderSettingsDialog } from './FolderSettingsDialog';
import { useCanSetItemDepartments } from '@/components/library/item-departments-field';
import { FolderListError } from '@/components/library/folder-picker-states';
import { StarButton } from '@/components/library/star-button';
import { apiClient } from '@/lib/api-client';
import { LIBRARY_DOCUMENTS_MAX_PAGE_SIZE, wikiArticlesQueryKey } from '@/lib/use-library-documents';
import type { WikiArticleRow } from '@/types/library';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface KBFolder {
  id: string;
  name: string;
  parentId: string | null;
  icon?: string | null;
  /**
   * How many documents of every kind this person may see in the folder — the
   * folders response's `count` (FolderSchema), the badge. Absent: no badge.
   */
  count?: number;
}

export interface KBArticleNode {
  id: string;
  title: string;
  parentId: string | null;
  folderId: string | null;
  children: KBArticleNode[];
}

interface KbSidebarProps {
  folders: KBFolder[];
  /** The folder list could not be read: say so (with a retry), never an empty tree. */
  foldersFailed?: { onRetry: () => void } | null;
  activeFolder: KBFolder | null;
  activeDocId: string | null;
  /** "All Documents": the server's total; null while unread or when the read failed (no number). */
  totalDocCount: number | null;
  onSelectFolder: (folder: KBFolder | null) => void;
  /**
   * Open a page. `folderId` is the folder the page is listed in — the folder
   * whose tree was clicked — so the reader's Previous / Next walk THAT folder's
   * list, not the active folder's (LB6T2-BUG-01).
   */
  onSelectDoc: (docId: string, folderId: string) => void;
  onCreateFolder: () => void;
  onCreateDoc: (parentId: string | null, folderId?: string | null) => void;
  onDeleteDoc: (docId: string, title: string) => void;
  onDeleteFolder: (folderId: string) => void;
  /** LIB-09 — open ShareFolderDialog for this folder. Optional so the
   *  sidebar can be reused in surfaces that don't expose sharing. */
  onShareFolder?: (folderId: string, folderName: string) => void;
}

// ─── Build tree from flat article list ───────────────────────────────────────

export function buildArticleTree(articles: { id: string; title: string; parentId: string | null; folderId: string | null }[]): KBArticleNode[] {
  const nodeMap = new Map<string, KBArticleNode>();
  const roots: KBArticleNode[] = [];

  // Create nodes
  for (const a of articles) {
    nodeMap.set(a.id, { id: a.id, title: a.title, parentId: a.parentId, folderId: a.folderId, children: [] });
  }

  // Build hierarchy
  for (const node of nodeMap.values()) {
    if (node.parentId && nodeMap.has(node.parentId)) {
      nodeMap.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

// ─── A folder's pages — the server's list for that folder ─────────────────────

/** The pages (articles) in one folder, as the server lists them; asked only while the folder is open. */
function useFolderPages(folderId: string, enabled: boolean) {
  return useQuery<{ data: WikiArticleRow[]; total: number }, Error>({
    queryKey: [...wikiArticlesQueryKey, 'tree', folderId] as const,
    queryFn: async () => {
      const res = await apiClient.get<{ data: WikiArticleRow[]; total: number }>(
        `/wiki/articles?folderId=${encodeURIComponent(folderId)}&pageSize=${LIBRARY_DOCUMENTS_MAX_PAGE_SIZE}`,
      );
      if (!res || !Array.isArray(res.data) || typeof res.total !== 'number') {
        throw new Error('The pages in this folder could not be read.');
      }
      return res;
    },
    enabled,
    staleTime: 0,
  });
}

function FolderPages({
  folderId, activeDocId, canWriteArticles, onSelectDoc, onCreateDoc, onDeleteDoc,
}: {
  folderId: string;
  activeDocId: string | null;
  canWriteArticles: boolean;
  onSelectDoc: (id: string, folderId: string) => void;
  onCreateDoc: (parentId: string | null, folderId?: string | null) => void;
  onDeleteDoc: (docId: string, title: string) => void;
}) {
  const pagesQ = useFolderPages(folderId, true);
  // Every page in this tree is listed in this folder.
  const selectInThisFolder = (id: string) => onSelectDoc(id, folderId);
  if (pagesQ.isError) {
    return (
      <div role="alert" className="ml-2 px-2 py-1 text-[11px] text-destructive" data-testid="folder-pages-error">
        The pages in this folder could not be loaded.{' '}
        <button type="button" className="underline" onClick={() => void pagesQ.refetch()} title="Load the pages again">
          Retry
        </button>
      </div>
    );
  }
  if (!pagesQ.data) return null;
  const rows = pagesQ.data.data;
  const tree = buildArticleTree(
    rows.map((a) => ({ id: a.id, title: a.title, parentId: a.parentId ?? null, folderId: a.folderId })),
  );
  const more = pagesQ.data.total - rows.length;
  return (
    <div className="ml-2">
      {tree.map(doc => (
        <TreeItem
          key={doc.id}
          node={doc}
          activeDocId={activeDocId}
          level={1}
          canWriteArticles={canWriteArticles}
          onSelectDoc={selectInThisFolder}
          onCreateChild={(parentId) => onCreateDoc(parentId, folderId)}
          onDelete={onDeleteDoc}
        />
      ))}
      {more > 0 && (
        <p className="px-2 py-1 text-[11px] text-muted-foreground">
          {more} more {more === 1 ? 'page' : 'pages'} — open the folder to see every document
        </p>
      )}
    </div>
  );
}

// ─── Folder icon helper ──────────────────────────────────────────────────────

function FolderIcon({ icon, isActive }: { icon?: string | null; isActive: boolean }) {
  switch (icon) {
    case 'code': return <Code2 className="h-4 w-4 shrink-0" />;
    default: return isActive ? <FolderOpen className="h-4 w-4 shrink-0" /> : <Folder className="h-4 w-4 shrink-0" />;
  }
}

// ─── TreeItem — Recursive document node ──────────────────────────────────────

function TreeItem({
  node, activeDocId, level, canWriteArticles, onSelectDoc, onCreateChild, onDelete,
}: {
  node: KBArticleNode;
  activeDocId: string | null;
  level: number;
  /** write:wiki — add a sub-page (POST /wiki/articles), delete a page (DELETE /wiki/articles/:id). */
  canWriteArticles: boolean;
  onSelectDoc: (id: string) => void;
  onCreateChild: (parentId: string) => void;
  onDelete: (id: string, title: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const isActive = activeDocId === node.id;

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-1 py-1 px-2 rounded-md cursor-pointer text-sm transition-colors',
          isActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted text-muted-foreground hover:text-foreground'
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelectDoc(node.id)}
      >
        {/* Expand/collapse chevron */}
        {hasChildren ? (
          <button
            className="shrink-0 p-0.5 hover:bg-muted rounded"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate flex-1 text-xs">{node.title}</span>

        {/* Actions menu — only for a person who may write articles: every
            item in it is an article write (L6-04). */}
        {canWriteArticles && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100" aria-label="Page actions" title="Actions">
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onCreateChild(node.id); }} className="text-xs">
                <Plus className="h-3 w-3 mr-2" /> Add sub-page
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(node.id, node.title); }} className="text-xs text-destructive">
                <Trash2 className="h-3 w-3 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Children (recursive) */}
      {hasChildren && expanded && (
        <div>
          {node.children.map(child => (
            <TreeItem
              key={child.id}
              node={child}
              activeDocId={activeDocId}
              level={level + 1}
              canWriteArticles={canWriteArticles}
              onSelectDoc={onSelectDoc}
              onCreateChild={onCreateChild}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Sidebar Component ──────────────────────────────────────────────────

export function KbSidebar({
  folders, foldersFailed, activeFolder, activeDocId, totalDocCount,
  onSelectFolder, onSelectDoc, onCreateFolder, onCreateDoc, onDeleteDoc, onDeleteFolder,
  onShareFolder,
}: KbSidebarProps) {
  // Library LB6-T3 / L6-04 — each control asks EXACTLY the key its route
  // declares (LIBRARY_CONTROLS in @argusai/shared); one it may not use is
  // not drawn:
  //   create / delete a folder          → write:library.documents (POST / DELETE /wiki/folders)
  //   share a folder                    → share:library (POST /library/acl/folders/:id/grants)
  //   add a page, add a sub-page, delete a page → write:wiki (POST / DELETE /wiki/articles)
  //   Settings (LB6-T8)                 → opens FolderSettingsDialog, whose fields each ask their own
  //                                       route's key: name / classification write:library.documents
  //                                       (PUT /wiki/folders/:id), departments share:library + read:users
  //                                       (PUT /library/acl/folders/:id/departments, the list from
  //                                       GET /users/departments); offered to a person who may use
  //                                       either, and the dialog draws only theirs. A folder is
  //                                       RENAMED there — the one write path for its name (LB6-T8
  //                                       review R2 deleted the menu's own Rename dialog).
  const canWriteFolders = useHasPermission('write', 'library.documents');
  const canShareFolders = useHasPermission('share', 'library');
  const canWriteArticles = useHasPermission('write', 'wiki');
  const canSetFolderDepartments = useCanSetItemDepartments();
  const canOpenFolderSettings = canWriteFolders || canSetFolderDepartments;
  // Delete needs write:library.documents, Add page write:wiki, Share
  // share:library, Settings a field it can draw — a menu with none of them is
  // not drawn.
  const folderMenuHasItems = canWriteFolders || canWriteArticles || (canShareFolders && !!onShareFolder) || canOpenFolderSettings;
  const [settingsFolderId, setSettingsFolderId] = useState<string | null>(null);
  // The folders whose page trees are open. A tree is one GET /wiki/articles
  // for that folder, so only the folders the person opens — the active one,
  // and any expanded with the chevron — ask for theirs. Every folder used to
  // start open, which fired one request per non-empty folder on load and
  // again after every write (LB6T2-BUG-03).
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    () => new Set(activeFolder ? [activeFolder.id] : []),
  );

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      next.has(folderId) ? next.delete(folderId) : next.add(folderId);
      return next;
    });
  };

  return (
    // #224 — hidden on narrow viewports (< md, 768px). At ~599px the
    // folder rail (256px) + editor metadata sidebar (300px) consumed
    // the viewport and pushed the editor body off-screen. md+ keeps
    // the rail visible; below md, users navigate via the documents
    // list view (which fills the available width).
    <div className="hidden md:flex w-64 border-r bg-card flex-col shrink-0">
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between">
        <span className="text-sm font-semibold">Folders</span>
        {canWriteFolders && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCreateFolder} aria-label="Create folder" title="Create folder">
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Radix's viewport wraps its children in a `display: table` div, which
          lets a row grow to its longest folder name instead of truncating it,
          pushing each folder's count badge past the sidebar's edge (LB-6
          prove E1b: every badge drawn outside the visible area). A block
          wrapper keeps the rows the sidebar's width. */}
      <ScrollArea className="flex-1 [&_[data-radix-scroll-area-viewport]>div]:block!">
        <div className="p-2 space-y-0.5">
          {/* The folder list could not be read: an error, not an empty tree. */}
          {foldersFailed ? (
            <div className="px-2 py-1">
              <FolderListError onRetry={foldersFailed.onRetry} data-testid="sidebar-folder-list-error" />
            </div>
          ) : null}
          {/* Folder list with document trees inside */}
          {folders.map(folder => {
            const isActive = activeFolder?.id === folder.id;
            const isExpanded = expandedFolders.has(folder.id);
            // #189 — the badge counts every kind (articles, uploads, synced):
            // the folders response's count, under the list's predicate.
            const folderBadgeCount = folder.count;

            return (
              <div key={folder.id}>
                {/* Folder header. #234 — was a bare <div> with onClick;
                    Codex / Playwright's role-based selectors couldn't
                    find the click target reliably (and bare <div>
                    onClick is poor a11y to begin with). The folder
                    select action is now a real `<button>` with
                    aria-label + aria-pressed taking the bulk of the
                    row, with the chevron and dropdown as separate
                    sibling buttons (HTML forbids nested buttons). */}
                <div
                  className={cn(
                    'group flex items-center gap-1 px-1 py-1 rounded-md text-sm transition-colors',
                    isActive ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted text-foreground'
                  )}
                >
                  <button
                    type="button"
                    className={cn('shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/5', isActive && 'hover:bg-white/10')}
                    aria-label={isExpanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
                    onClick={(e) => { e.stopPropagation(); toggleFolder(folder.id); }}
                  >
                    {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    type="button"
                    aria-label={`Open folder ${folder.name}`}
                    aria-pressed={isActive}
                    className="flex-1 min-w-0 flex items-center gap-2 text-left px-1 py-1"
                    onClick={() => { onSelectFolder(folder); if (!isExpanded) toggleFolder(folder.id); }}
                  >
                    <FolderIcon icon={folder.icon} isActive={isActive} />
                    <span className="truncate flex-1 text-sm">{folder.name}</span>
                    {folderBadgeCount !== undefined && (
                      <Badge variant="outline" className={cn('text-[10px] h-4 px-1 shrink-0', isActive && 'border-primary-foreground/30 text-primary-foreground')}>{folderBadgeCount}</Badge>
                    )}
                  </button>

                  {/* The folder's star — a sibling button, kind 'folder'. On the
                      selected (bg-primary) row its unlit outline takes the
                      row's foreground, as the folder menu button does. */}
                  <StarButton item={{ id: folder.id, kind: 'folder' }} showOnHoverOnly onPrimary={isActive} className="h-5 w-5" />

                  {/* Folder menu — sibling, not nested. Drawn only when it holds
                      something this person may use. */}
                  {folderMenuHasItems && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className={cn('h-5 w-5 shrink-0', isActive && 'text-primary-foreground')} aria-label="Folder actions" title="Folder actions">
                        <MoreHorizontal className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {canWriteArticles && <DropdownMenuItem onClick={() => onCreateDoc(null, folder.id)} className="text-xs"><Plus className="h-3 w-3 mr-2" />Add page</DropdownMenuItem>}
                      {canShareFolders && onShareFolder && (
                        <DropdownMenuItem onClick={() => onShareFolder(folder.id, folder.name)} className="text-xs">
                          <Share2 className="h-3 w-3 mr-2" />Share
                        </DropdownMenuItem>
                      )}
                      {canOpenFolderSettings && (
                        <DropdownMenuItem onClick={() => setSettingsFolderId(folder.id)} className="text-xs">
                          <Settings className="h-3 w-3 mr-2" />Settings
                        </DropdownMenuItem>
                      )}
                      {canWriteFolders && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => onDeleteFolder(folder.id)} className="text-xs text-destructive">
                            <Trash2 className="h-3 w-3 mr-2" />Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                </div>

                {/* Page tree inside folder — the server's pages for this folder,
                    asked only when it is open and holds something. */}
                {isExpanded && (folder.count ?? 0) > 0 && (
                  <FolderPages
                    folderId={folder.id}
                    activeDocId={activeDocId}
                    canWriteArticles={canWriteArticles}
                    onSelectDoc={onSelectDoc}
                    onCreateDoc={onCreateDoc}
                    onDeleteDoc={onDeleteDoc}
                  />
                )}
              </div>
            );
          })}

          {/* "All Documents" at the bottom */}
          <div className="pt-2 mt-2 border-t">
            <button
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left',
                !activeFolder ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted text-muted-foreground'
              )}
              onClick={() => onSelectFolder(null)}
            >
              <BookOpen className="h-4 w-4 shrink-0" />
              <span>All Documents</span>
              {totalDocCount !== null && (
                <Badge variant="outline" className={cn('ml-auto text-[10px]', !activeFolder && 'border-primary-foreground/30 text-primary-foreground')} data-testid="all-documents-count">{totalDocCount}</Badge>
              )}
            </button>
          </div>
        </div>
      </ScrollArea>

      {/* Folder settings: name, classification, departments (LB6-T8) */}
      <FolderSettingsDialog
        open={settingsFolderId !== null}
        onOpenChange={(open) => { if (!open) setSettingsFolderId(null); }}
        folderId={settingsFolderId}
      />
    </div>
  );
}
